import { phone } from '@vette/common-utils';
import {
  ConfirmationResult,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updatePhoneNumber,
} from 'firebase/auth';
import { useCallback, useRef, useState } from 'react';

import { auth } from './initializeAuth';

type Status =
  | 'initial'
  | 'verification_loading'
  | 'verification'
  | 'confirming'
  | 'confirmed';

export const usePhoneAuthentication = () => {
  const [status, setStatus] = useState<Status>('initial');
  const [confirmationResult, setConfirmationResult] =
    useState<ConfirmationResult>();
  const [verificationId, setVerificationId] = useState<string>();
  const verifierInstance = useRef<RecaptchaVerifier>();

  const setNewRecaptchaVerifierInstance = useCallback(() => {
    return new Promise<void>(resolve => {
      try {
        verifierInstance.current = new RecaptchaVerifier(
          'verification-button',
          {
            size: 'invisible',
          },
          auth
        );
        resolve();
      } catch (e) {
        console.error(e);
      }
    });
  }, []);

  const reset = useCallback(() => {
    setStatus('initial');
    setConfirmationResult(undefined);
  }, [setConfirmationResult]);

  const authenticate = useCallback(async (phoneNumber: string) => {
    if (!verifierInstance.current)
      throw new Error('Failed to initialize reCAPTCHA');

    setStatus('verification_loading');
    const confirmationResult = await signInWithPhoneNumber(
      auth,
      phone.normalize(phoneNumber),
      verifierInstance.current
    ).catch(e => {
      setStatus('initial');
      throw e;
    });

    setStatus('verification');

    setConfirmationResult(confirmationResult);
  }, []);

  const verifyCode = useCallback(
    async (code: string) => {
      if (!confirmationResult) {
        throw new Error('Phone number was not provided.');
      }

      if (!code) {
        throw new Error('Verification code was not provided.');
      }

      setStatus('confirming');
      const userCredentials = await confirmationResult
        .confirm(code)
        .catch(e => {
          setStatus('verification');
          throw e;
        });
      setStatus('confirmed');

      return userCredentials.user;
    },
    [confirmationResult]
  );

  const sendVerificationCode = useCallback(async (phoneNumber: string) => {
    if (!verifierInstance.current)
      throw new Error('Failed to initialize reCAPTCHA');
    const provider = new PhoneAuthProvider(auth);

    setStatus('verification_loading');
    const verificationId = await provider
      .verifyPhoneNumber({ phoneNumber }, verifierInstance.current)
      .catch(e => {
        setStatus('initial');
        throw e;
      });
    setStatus('verification');

    setVerificationId(verificationId);
  }, []);

  const updatePhone = useCallback(
    async (verificationCode: string) => {
      if (!verificationId) {
        throw new Error('Phone number was not provided.');
      } else if (!auth.currentUser) {
        throw new Error('User is not signed in');
      }

      const credential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );

      await updatePhoneNumber(auth.currentUser, credential);
      return auth.currentUser;
    },
    [verificationId]
  );

  return {
    wasPhoneNumberProvided: !!confirmationResult || !!verificationId,
    isVerificationLoading: status === 'verification_loading',
    setNewRecaptchaVerifierInstance,
    /* login, signup, acceptInvitation */
    authenticate,
    verifyCode,
    reset,
    /* changePhone */
    sendVerificationCode,
    updatePhone,
  };
};
